// Facilities.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../Loader";
import EmptyFacilities from "./EmptyFacilities";
import AddFacility from "./Facility/AddFacility";
import EditFacility from "./Facility/EditFacility";
import DeleteFacility from "./Facility/DeleteFacility";
import "./addons.css";
import AuthContext from "../../contexts/AuthProvider";
import { useContext } from "react";
import { getUserRole, USER_ROLES } from "../../utils/UsersPermissions";
import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

const Facilities = () => {
  const { t } = useTranslation("addons");
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("allTypes");
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const actionsDropdownRef = useRef(null);
  const { isAuthenticated } = useContext(AuthContext);

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  const fetchFacilities = async () => {
    try {
      if (!isAuthenticated) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }

      const response = await fetch(`${baseUrl}/api/v1/attnd-venue/facilities`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (response.status === 401) {
        navigate("/login");
        throw new Error(t("errors.unauthorized"));
      }
      if (!response.ok) {
        throw new Error(t("errors.fetchFailed"));
      }

      const invitation = await response.json();
      return invitation.data;
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      }
      return [];
    }
  };

  useEffect(() => {
    const getFacilities = async () => {
      setLoading(true);
      const facilityList = await fetchFacilities();
      setFacilities(facilityList);
      setLoading(false);
    };
    getFacilities();
  }, []);

  const transformedFacilities = facilities.map((facility) => ({
    id: facility.id,
    name: facility.tool_name || t("unknown"),
    type: facility.type || t("unknown"),
    cost: facility.cost || t("unknown"),
    venue_id: facility.venue_id,
    duration: facility.duration || t("unknown"),
  }));

  const filteredFacilities = transformedFacilities.filter((facility) => {
    const matchesSearch =
      searchTerm === "" ||
      facility.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "allTypes" || facility.type === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDropdown = (id) => {
    setActiveDropdown((prevId) => (prevId === id ? null : id));
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        actionsDropdownRef.current &&
        !actionsDropdownRef.current.contains(event.target) &&
        !event.target.closest(".details-dropdown")
      ) {
        setActiveDropdown(null);
      }
    };
    if (activeDropdown !== null) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [activeDropdown]);

  const handleAction = (act, id, venue_id) => {
    setAction(act);
    setSelectedFacility(id);
    setSelectedVenue(venue_id);
  };

  const refreshFacilities = async () => {
    const data = await fetchFacilities();
    setFacilities(data || []);
  };

  const closeForm = () => {
    refreshFacilities();
    setAction(null);
  };

  if (loading) {
    return (
      <div className="addons-loader-container">
        <Loader />
      </div>
    );
  }

  if (facilities.length > 0) {
    return (
      <div className="addons-container">
        <div className="addon-actions">
          <div className="controls-row">
            <div className="search-box">
              <img src="/search-normal.svg" alt="search illustration" />
              <input
                type="text"
                placeholder={t("facilities.searchPlaceholder")}
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="controls-actions">
              <div className="status-select">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="allTypes">
                    {t("facilities.statusFilter.allTypes")}
                  </option>
                  <option value="Equipment">
                    {t("options.type.Equipment", { defaultValue: "Equipment" })}
                  </option>
                  <option value="Audio">
                    {t("options.type.Audio", { defaultValue: "Audio" })}
                  </option>
                </select>
              </div>
              {/* Hide add facility button for viewers */}
              {getUserRole() !== USER_ROLES.VIEWER && (
                <button className="add-addon" onClick={() => setAction("add")}>
                  <img src="/add.svg" alt="add illustration" />
                  {t("facilities.buttons.addFacility")}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Facilities Table */}
        <div className="addon-table table-wrapper">
          <table className="addons-table">
            <thead>
              <tr>
                <th>{t("facilities.tableHeaders.toolName")}</th>
                <th>{t("facilities.tableHeaders.type")}</th>
                <th>{t("facilities.tableHeaders.cost")}</th>
                {/* Hide action header for viewers */}
                {getUserRole() !== USER_ROLES.VIEWER && (
                  <th>{t("facilities.tableHeaders.action")}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredFacilities.length > 0 ? (
                filteredFacilities.map((facility) => (
                  <tr key={facility.id}>
                    <td>{facility.name}</td>
                    <td>
                      {t(`options.type.${facility.type}`, {
                        defaultValue: facility.type,
                      })}
                    </td>
                    <td>
                      {facility.cost} {t("facilities.currency")}
                    </td>
                    {/* Hide action buttons for viewers */}
                    {getUserRole() !== USER_ROLES.VIEWER && (
                      <td>
                        <button
                          onClick={() => handleDropdown(facility.id)}
                          className="action-details"
                        >
                          <img src="/more-circle.svg" alt="Details icon" />
                          {activeDropdown === facility.id && (
                            <ul
                              className="details-dropdown"
                              ref={actionsDropdownRef}
                            >
                              <li>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleAction(
                                      "edit",
                                      facility.id,
                                      facility.venue_id
                                    )
                                  }
                                >
                                  {t("facilities.buttons.edit")}
                                </button>
                              </li>
                              <li>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleAction(
                                      "delete",
                                      facility.id,
                                      facility.venue_id
                                    )
                                  }
                                >
                                  {t("facilities.buttons.delete")}
                                </button>
                              </li>
                            </ul>
                          )}
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={getUserRole() !== USER_ROLES.VIEWER ? "4" : "3"}
                    className="no-data"
                  >
                    {t("facilities.noData")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="addon-mobile-table">
          {filteredFacilities.length > 0 ? (
            <ul>
              {filteredFacilities.map((facility) => (
                <li key={facility.id} className="addon-card">
                  <div className="addon-card-content">
                    <div className="addon-main-info">
                      <span>{facility.name}</span>
                      <span className="addon-cost">
                        {facility.cost} {t("facilities.currency")}
                      </span>
                    </div>
                    <div className="addon-info">
                      {t(`options.type.${facility.type}`, {
                        defaultValue: facility.type,
                      })}
                      , {facility.duration}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div>
              <div className="no-data">{t("facilities.noData")}</div>
            </div>
          )}
        </div>

        {action === "add" && <AddFacility onClose={closeForm} />}
        {action === "edit" && (
          <EditFacility
            onClose={closeForm}
            facility_id={selectedFacility}
            venue_id={selectedVenue}
          />
        )}
        {action === "delete" && (
          <DeleteFacility
            onClose={closeForm}
            facility_id={selectedFacility}
            venue_id={selectedVenue}
          />
        )}
      </div>
    );
  } else if (!loading) {
    return (
      <>
        {/* Pass the lowercase prop name */}
        <EmptyFacilities addFacility={() => setAction("add")} />
        {action === "add" && <AddFacility onClose={closeForm} />}
      </>
    );
  }
};

export default Facilities;
